import Graphemer from 'graphemer'
import twitterText from 'twitter-text'

export {
  getProtectedTextRanges,
  getThreadSplitBoundaryCandidates,
  MAX_THREAD_ITEMS,
  splitTextIntoThreadRanges,
  ThreadSplitBoundaryKind,
  ThreadSplitRangeErrorCode,
  ThreadSplitRangeValidationErrorCode,
  validateThreadSplitRanges,
} from './threadSplit'
export type {
  ThreadSplitBoundaryCandidate,
  ThreadSplitBoundaryKindValue,
  ThreadSplitOptions,
  ThreadSplitRangesResult,
  ThreadSplitRangeValidationResult,
  Utf16Range,
} from './threadSplit'

const graphemer = new Graphemer()

/**
 * Counts `text` by Unicode grapheme cluster rather than UTF-16 code unit, so
 * multi-code-unit characters (ZWJ sequences, flag emoji, combining marks)
 * count as one character each.
 */
function graphemeCount(text: string): number {
  return graphemer.splitGraphemes(text).length
}

function getPreviousCodePointStart(text: string, offset: number): number {
  const lastCodeUnitIndex = offset - 1
  if (lastCodeUnitIndex < 0) {
    return 0
  }

  const lastCodeUnit = text.charCodeAt(lastCodeUnitIndex)
  const previousCodeUnit = text.charCodeAt(lastCodeUnitIndex - 1)
  const endsWithSurrogatePair =
    lastCodeUnit >= 0xdc00 &&
    lastCodeUnit <= 0xdfff &&
    previousCodeUnit >= 0xd800 &&
    previousCodeUnit <= 0xdbff

  return endsWithSurrogatePair ? lastCodeUnitIndex - 1 : lastCodeUnitIndex
}

// Platform-specific URL character count constants
const BLUESKY_URL_PATH_MAX_LENGTH = 16
const LINKEDIN_URL_CHAR_COUNT = 24
const MASTODON_URL_CHAR_COUNT = 23
const MASTODON_URL_PLACEHOLDER = 'x'.repeat(MASTODON_URL_CHAR_COUNT)
// Mastodon's Extractor::MAX_DOMAIN_LENGTH - past this a mention isn't
// federated, so it counts in full instead of being discounted.
const MASTODON_MAX_DOMAIN_LENGTH = 253

// Matches Mastodon's federated mention syntax (@user@instance.tld): Ruby's
// [[:word:]] is Unicode-aware (JS \w isn't) and includes combining marks, so
// both the domain and the boundary checks use the same Unicode word class -
// otherwise a preceding accented letter or a decomposed accent within the
// domain would go unmatched.
const MASTODON_WORD_CHAR_CLASS = '\\p{L}\\p{N}\\p{M}_'
const MASTODON_USERNAME_CHARS = '[a-zA-Z0-9_]+(?:[.-]+[a-zA-Z0-9_]+)*'
const MASTODON_DOMAIN_CHARS = `[${MASTODON_WORD_CHAR_CLASS}]+(?:[.-]+[${MASTODON_WORD_CHAR_CLASS}]+)*`
const MASTODON_INVALID_MENTION_PREFIX = new RegExp(
  `[=/${MASTODON_WORD_CHAR_CLASS}]$`,
  'u',
)
const MASTODON_MENTION_PATTERN = new RegExp(
  `@(${MASTODON_USERNAME_CHARS}@${MASTODON_DOMAIN_CHARS})`,
  'gu',
)

/**
 * Checks if a URL has valid Bluesky boundaries.
 * Bluesky requires URLs to be preceded by: start of string, whitespace, or opening parenthesis.
 * This matches Bluesky's ATProto SDK URL detection pattern: /(^|\s|\()((https?:\/\/...)/
 *
 * Uses Array.from to iterate over Unicode code points (not UTF-16 code units), so that
 * multi-code-unit emoji before a URL are correctly identified as non-boundary characters.
 *
 * @param text - The full text containing the URL
 * @param urlStartIndex - The starting UTF-16 index of the URL in the text
 * @returns true if the URL has valid Bluesky boundaries
 */
export function hasValidBlueskyUrlBoundary(
  text: string,
  urlStartIndex: number,
): boolean {
  if (urlStartIndex === 0) {
    return true
  }
  // Slice the text before the URL and get the last full Unicode character
  // (not just the last UTF-16 code unit, which would be wrong for surrogate pairs)
  const textBeforeUrl = text.slice(0, urlStartIndex)
  const chars = [...textBeforeUrl]
  const charBefore = chars[chars.length - 1]
  return charBefore !== undefined && /[\s(]/.test(charBefore)
}

/**
 * Supported service types for character counting.
 * This type accepts both string literals and enum values for flexibility.
 */
export type CharCountService =
  | 'twitter'
  | 'bluesky'
  | 'instagram'
  | 'linkedin'
  | 'mastodon'
  | 'facebook'
  | 'googlebusiness'
  | 'pinterest'
  | 'threads'
  | 'tiktok'
  | 'youtube'
  | 'startPage'
  | 'substack'
  | 'whatsapp'

/**
 * Text length utilities for character counting.
 *
 * This module provides methods for calculating text length consistently
 * across the application, accounting for platform-specific rules.
 *
 * TODO: WE NEED TO ADD HERE THE LOGIC FOR ADDITIONAL CHARACTERS USED WHEN THE USER
 * ATTACHES A MEDIA TO THE POST.
 */

/**
 * Gets the length of text using JavaScript's default string length.
 * This counts UTF-16 code units, meaning emojis and some special characters
 * may count as multiple characters.
 *
 * @param text - The text to measure
 * @returns The length in UTF-16 code units
 */
export function getCharsCount(text: string): number {
  return text.length
}

/**
 * Gets the length of text using Twitter's character counting rules.
 * Uses the twitter-text library which implements Twitter's official counting:
 * - URLs count as a fixed length (23 characters for http and https)
 * - Emojis count as 2 characters
 * - Mentions and hashtags are counted correctly
 * - Other platform-specific rules
 *
 * @param text - The text to measure
 * @returns The length according to Twitter's rules
 */
export function getTwitterCharsCount(text: string): number {
  return twitterText.getTweetLength(text)
}

/**
 * Gets the length of text using Bluesky's character counting rules.
 * Uses grapheme splitter for accurate emoji counting and special URL handling.
 *
 * @param text - The text to measure
 * @returns The length according to Bluesky's rules
 */
export function getBlueskyCharsCount(text: string): number {
  // Use grapheme clusters for accurate emoji counting (Unicode 15+, matching ATProto)
  let charCount = graphemeCount(text)

  const urls = twitterText.extractUrlsWithIndices(text, {
    extractUrlsWithoutProtocol: false,
  })

  // Filter URLs to only include those with valid Bluesky boundaries
  // Bluesky requires URLs to be preceded by start of string, whitespace, or opening parenthesis
  const validUrls = urls.filter(
    (urlWithIndices: { url: string; indices: [number, number] }) =>
      hasValidBlueskyUrlBoundary(text, urlWithIndices.indices[0]),
  )

  // If there are valid urls in the text, handle each URL's length
  if (validUrls.length > 0) {
    validUrls.forEach(
      (urlWithIndices: { url: string; indices: [number, number] }) => {
        try {
          const url = new URL(urlWithIndices.url)

          charCount -= urlWithIndices.url.length

          charCount +=
            url.host.length +
            Math.min(
              url.pathname.replace(/^\/$/, '').length +
                url.search.length +
                url.hash.length,
              BLUESKY_URL_PATH_MAX_LENGTH,
            )
        } catch {
          // Invalid URL, keep original length
        }
      },
    )
  }

  return charCount
}

/**
 * Gets the length of text for Instagram.
 * Instagram counts line breaks as 2 characters.
 *
 * @param text - The text to measure
 * @returns The length according to Instagram's rules
 */
export function getInstagramCharsCount(text: string): number {
  const charCount = text.length
  let lineBreakLength = 0

  const lineBreakMatch = text.match(/\n/g)
  if (lineBreakMatch?.length) {
    lineBreakLength = lineBreakMatch?.length
  }
  return charCount + lineBreakLength
}

/**
 * Gets the length of text for LinkedIn.
 * LinkedIn counts URLs as 24 characters regardless of actual length.
 *
 * @param text - The text to measure
 * @returns The length according to LinkedIn's rules
 */
export function getLinkedInCharsCount(text: string): number {
  let charCount = text.length

  const urls = twitterText.extractUrls(text) ?? []

  // If there are urls in the text, subtract the url length
  // from the charCount and add back the LinkedIn URL char count for each url
  if (urls.length > 0) {
    let totalUrlLength = 0

    urls.forEach((url) => {
      totalUrlLength += url.length
    })

    charCount -= totalUrlLength
    charCount += urls.length * LINKEDIN_URL_CHAR_COUNT
  }

  return charCount
}

type MastodonTextEntity = {
  start: number
  end: number
  replacement: string
}

// Mirrors Mastodon's countable_text: splice each URL/mention for its
// counted-length placeholder instead of adjusting a running total, so the
// result can be joined with spoiler text and grapheme-counted as one string.
function rewriteMastodonBody(text: string): string {
  const urlsWithIndices = twitterText.extractUrlsWithIndices(text, {
    extractUrlsWithoutProtocol: false,
  })
  const urlEntities: MastodonTextEntity[] = urlsWithIndices.map(
    (urlWithIndices: { indices: [number, number] }) => ({
      start: urlWithIndices.indices[0],
      end: urlWithIndices.indices[1],
      replacement: MASTODON_URL_PLACEHOLDER,
    }),
  )

  const mentionEntities: MastodonTextEntity[] = [
    ...text.matchAll(MASTODON_MENTION_PATTERN),
  ]
    .map((mention): MastodonTextEntity | null => {
      const start = mention.index ?? 0
      const end = start + mention[0].length
      if (MASTODON_INVALID_MENTION_PREFIX.test(text.slice(0, start)))
        return null

      // A URL can contain mention-shaped text (e.g. a profile link path like
      // /@user@instance.tld) - skip it here since the URL entity above
      // already accounts for those characters.
      const overlapsUrl = urlEntities.some(
        (url) => start < url.end && url.start < end,
      )
      if (overlapsUrl) return null

      const [localUsername, domain] = mention[1].split('@')
      // A domain longer than Mastodon allows isn't treated as federated, so
      // the mention counts in full rather than being discounted.
      if (domain.length > MASTODON_MAX_DOMAIN_LENGTH) return null

      return { start, end, replacement: `@${localUsername}` }
    })
    .filter((entity): entity is MastodonTextEntity => entity !== null)

  const entities = [...urlEntities, ...mentionEntities].toSorted(
    (a, b) => a.start - b.start,
  )

  let rewritten = ''
  let cursor = 0
  for (const entity of entities) {
    rewritten += text.slice(cursor, entity.start) + entity.replacement
    cursor = entity.end
  }
  rewritten += text.slice(cursor)

  return rewritten
}

/**
 * Gets the combined length of a Mastodon status: spoiler text joined with
 * the URL/mention-substituted body, counted together in one grapheme pass -
 * matching Mastodon's StatusLengthValidator, so a grapheme spanning the two
 * (e.g. half a flag emoji at the end of the spoiler) counts once, not twice.
 *
 * @param text - The post body
 * @param spoilerText - The content warning text, if any
 * @returns The combined length according to Mastodon's rules
 */
export function getMastodonStatusCharsCount(
  text: string,
  spoilerText = '',
): number {
  return graphemeCount(spoilerText + rewriteMastodonBody(text))
}

/**
 * Gets the length of a Mastodon post body: grapheme-counted, URLs shortened
 * to 23 characters, federated mentions discounted to their local `@user`
 * part. Delegates to getMastodonStatusCharsCount with no spoiler text.
 *
 * @param text - The text to measure
 * @returns The length according to Mastodon's rules
 */
export function getMastodonCharsCount(text: string): number {
  return getMastodonStatusCharsCount(text)
}

/**
 * Gets the extra characters Mastodon spoiler text adds on top of the body
 * count. A caller that already has a per-service "additional length" hook
 * (added to the body count separately, e.g. for an attachment) can plug
 * this in directly: the delta is defined so body count + this always equals
 * getMastodonStatusCharsCount(text, spoilerText) exactly, even when a
 * grapheme would span the spoiler/body join.
 *
 * @param text - The post body
 * @param spoilerText - The content warning text, if any
 * @returns The additional length to add on top of getMastodonCharsCount(text)
 */
export function getMastodonSpoilerDelta(
  text: string,
  spoilerText: string,
): number {
  if (!spoilerText) return 0
  return (
    getMastodonStatusCharsCount(text, spoilerText) - getMastodonCharsCount(text)
  )
}

/**
 * Gets the length of text for Threads.
 * The Threads API counts its 500 limit in Unicode code points and rejects
 * anything over with "Param text must be at most 500 characters long".
 * Measured against the live API (CORE-8053): 500 Japanese, Cyrillic or
 * accented Latin characters are accepted and 501 are not; an emoji counts
 * as 1 (skin-tone modifiers add 1 more); an Indic combining mark counts on
 * its own, so 250 graphemes of "कि" are 500. Meta's docs say emoji count as
 * their UTF-8 bytes, but the API does not.
 *
 * @param text - The text to measure
 * @returns The number of code points, which is what the Threads API counts
 */
export function getThreadsCharsCount(text: string): number {
  return [...text].length
}

/**
 * Finds the UTF-16 offset in `text` where `countText` last stays within
 * `limit`, snapped to a grapheme boundary so it never splits one (a
 * surrogate pair, ZWJ sequence, flag emoji, combining mark).
 *
 * Keeps scanning past the first over-limit prefix rather than stopping
 * there: entity-substitution counters (URL/mention shortening) aren't
 * monotonic - an incomplete URL counts at full literal length until it
 * completes and gets discounted, so the count can dip back under the limit
 * later on. A URL is only treated this way if `countText` actually
 * discounts it (checked once per URL up front, with its original boundary
 * context) - Threads/Instagram/plain counters don't
 * shorten URLs at all, so their boundary can legitimately fall inside one,
 * and skipping it there would highlight characters that are still within
 * budget. For a URL that is discounted, its still-forming text is never
 * evaluated (twitter accepts URLs up to 4096 characters, so an inflated
 * mid-URL reading has no fixed bound to give up on, and re-running
 * `countText` per character would be needlessly expensive anyway), and its
 * exact known length - not an open-ended "a URL is somewhere ahead" - sets
 * how much further over the limit the scan tolerates before giving up. Once
 * every discounted URL ahead is accounted for, a non-URL entity (e.g. a
 * Mastodon mention, capped well under `limit`) stays covered by a fixed
 * margin of `limit` itself.
 *
 * @param text - The text to scan
 * @param countText - A counting function, e.g. getMastodonCharsCount
 * @param limit - The count threshold
 * @returns The offset after the last within-limit grapheme, or `text.length`
 * if the full text stays within the limit
 */
export function findCharacterLimitOffset(
  text: string,
  countText: (text: string) => number,
  limit: number,
): number {
  const discountedUrls: Array<{
    start: number
    end: number
    graphemeLength: number
  }> = twitterText
    .extractUrlsWithIndices(text, { extractUrlsWithoutProtocol: false })
    .map((url: { url: string; indices: [number, number] }) => {
      const contextStart = getPreviousCodePointStart(text, url.indices[0])
      const contextBeforeUrl = text.slice(contextStart, url.indices[0])
      const contextThroughUrl = text.slice(contextStart, url.indices[1])

      return {
        start: url.indices[0],
        end: url.indices[1],
        graphemeLength: graphemeCount(url.url),
        countedLength:
          countText(contextThroughUrl) - countText(contextBeforeUrl),
      }
    })
    .filter(
      (url: { graphemeLength: number; countedLength: number }) =>
        url.countedLength < url.graphemeLength,
    )

  let remainingReclaim = discountedUrls.reduce(
    (sum, url) => sum + url.graphemeLength,
    0,
  )
  const margin = Math.max(limit, 1)

  let offset = 0
  let lastOffsetWithinLimit = 0
  let nextUrlIndex = 0
  for (const grapheme of graphemer.splitGraphemes(text)) {
    const nextOffset = offset + grapheme.length
    offset = nextOffset

    while (
      nextUrlIndex < discountedUrls.length &&
      discountedUrls[nextUrlIndex].end <= nextOffset
    ) {
      remainingReclaim -= discountedUrls[nextUrlIndex].graphemeLength
      nextUrlIndex++
    }

    const currentUrl = discountedUrls[nextUrlIndex]
    const isInsideUrl =
      currentUrl !== undefined &&
      nextOffset > currentUrl.start &&
      nextOffset < currentUrl.end
    if (isInsideUrl) {
      continue
    }

    const count = countText(text.slice(0, nextOffset))
    if (count <= limit) {
      lastOffsetWithinLimit = nextOffset
      continue
    }

    if (count > limit + margin + remainingReclaim) {
      break
    }
  }
  return lastOffsetWithinLimit
}

/**
 * Gets the length of text using platform-specific counting rules.
 * Automatically selects the appropriate counting method based on the service.
 * This follows the same logic as DraftMethods.getTextCharacterCount.
 *
 * @param text - The text to measure
 * @param service - The social media service (twitter, facebook, etc.)
 * @returns The length according to the platform's rules
 */
export function getCharsCountForService(
  text: string,
  service: CharCountService,
): number {
  switch (service) {
    case 'twitter':
      return getTwitterCharsCount(text)
    case 'bluesky':
      return getBlueskyCharsCount(text)
    case 'instagram':
      return getInstagramCharsCount(text)
    case 'linkedin':
      return getLinkedInCharsCount(text)
    case 'mastodon':
      return getMastodonCharsCount(text)
    case 'threads':
      return getThreadsCharsCount(text)
    default:
      return getCharsCount(text)
  }
}

/**
 * Extracts the URLs contained in a piece of text, using the same Twitter
 * text rules the toolkit uses for character counting. Each occurrence is
 * returned (no deduplication).
 *
 * @param text - The text to scan for URLs
 * @returns The list of URLs found, in order
 */
export function extractUrls(text: string): string[] {
  return twitterText.extractUrls(text) ?? []
}
